import React from 'react';

// Re-export React Flow canvas from its own file
export { FlowCanvas } from './FlowCanvas';

// ─── Flow Gallery ───────────────────────────────────────────────
// Shows available WhatsApp conversation flows for the selected business type.

interface FlowGalleryProps {
  businessType?: string;
  selectedFlowId: string | null;
  onFlowSelect: (flowId: string) => void;
  showMetrics?: boolean;
  flows?: Array<{ id: string; name: string; description: string; steps: number }>;
}

const FLOW_DEFINITIONS: Record<string, Array<{ id: string; name: string; description: string; steps: number }>> = {
  barbershop: [
    { id: 'booking', name: 'Booking Flow', description: 'WhatsApp conversation flow for scheduling haircuts and beard trims', steps: 8 },
    { id: 'reschedule', name: 'Reschedule Flow', description: 'Customer-initiated booking reschedule via WhatsApp', steps: 5 },
    { id: 'cancel', name: 'Cancellation Flow', description: 'Booking cancellation with reason collection', steps: 4 },
    { id: 'reminder', name: 'Reminder Flow', description: 'Automated 24h appointment reminder with confirmation', steps: 3 },
  ],
  salon: [
    { id: 'booking', name: 'Booking Flow', description: 'Service selection, stylist preference, and time slot booking', steps: 10 },
    { id: 'reschedule', name: 'Reschedule Flow', description: 'Reschedule with stylist availability check', steps: 6 },
    { id: 'feedback', name: 'Feedback Flow', description: 'Post-visit satisfaction survey', steps: 4 },
    { id: 'reminder', name: 'Reminder Flow', description: 'Appointment reminder with prep instructions', steps: 3 },
  ],
  spa: [
    { id: 'booking', name: 'Booking Flow', description: 'Spa treatment booking with health questionnaire', steps: 9 },
    { id: 'package', name: 'Package Flow', description: 'Multi-session package purchase flow', steps: 6 },
    { id: 'reminder', name: 'Reminder Flow', description: 'Pre-visit instructions and arrival reminder', steps: 4 },
  ],
  clinic: [
    { id: 'booking', name: 'Consultation Booking', description: 'Medical consultation scheduling with intake form', steps: 8 },
    { id: 'followup', name: 'Follow-up Flow', description: 'Post-treatment follow-up and next steps', steps: 5 },
    { id: 'reminder', name: 'Appointment Reminder', description: 'Reminder with pre-visit preparation instructions', steps: 3 },
  ],
  'nail-salon': [
    { id: 'booking', name: 'Booking Flow', description: 'Nail service booking with design selection', steps: 7 },
    { id: 'maintenance', name: 'Maintenance Reminder', description: 'Gel/acrylic maintenance scheduling', steps: 4 },
    { id: 'reminder', name: 'Appointment Reminder', description: '24h reminder with nail prep tips', steps: 3 },
  ],
  masseuse: [
    { id: 'booking', name: 'Session Booking', description: 'Massage session booking with pressure preference', steps: 7 },
    { id: 'package', name: 'Package Flow', description: 'Multi-session discount package', steps: 5 },
    { id: 'reminder', name: 'Session Reminder', description: 'Pre-session hydration and arrival reminder', steps: 3 },
  ],
  retail: [
    { id: 'order', name: 'Order Flow', description: 'Product order via WhatsApp with payment', steps: 8 },
    { id: 'inquiry', name: 'Product Inquiry', description: 'Product availability and pricing questions', steps: 4 },
    { id: 'tracking', name: 'Order Tracking', description: 'Delivery status updates', steps: 3 },
  ],
};

export function FlowGallery({ businessType, selectedFlowId, onFlowSelect, showMetrics, flows: flowsProp }: FlowGalleryProps): JSX.Element {
  const flows = flowsProp || FLOW_DEFINITIONS[businessType || 'barbershop'] || FLOW_DEFINITIONS.barbershop;

  return React.createElement('div', {
    className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
    'data-testid': 'flow-gallery',
  },
    flows.map((flow) =>
      React.createElement('button', {
        key: flow.id,
        onClick: () => onFlowSelect(flow.id),
        className: `text-left p-4 rounded-lg border transition-all hover:shadow-md hover:border-primary/50 ${
          selectedFlowId === flow.id ? 'border-primary bg-primary/5' : 'border-border'
        }`,
        'data-testid': `flow-card-${flow.id}`,
      },
        React.createElement('h3', { className: 'font-semibold text-sm mb-1' }, flow.name),
        React.createElement('p', { className: 'text-xs text-muted-foreground mb-2' }, flow.description),
        React.createElement('div', { className: 'flex items-center gap-2 text-xs text-muted-foreground' },
          React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted' },
            `${flow.steps} steps`
          ),
          showMetrics && React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300' },
            'Active'
          ),
        ),
      ),
    ),
  );
}

// ─── Flow Legend ─────────────────────────────────────────────────

interface FlowLegendProps {
  defaultExpanded?: boolean;
  className?: string;
}

export function FlowLegend({ defaultExpanded, className }: FlowLegendProps): JSX.Element {
  const items = [
    { color: 'bg-blue-400', label: 'Start / Entry point' },
    { color: 'bg-green-400', label: 'Interaction (user input)' },
    { color: 'bg-yellow-400', label: 'Decision point' },
    { color: 'bg-purple-400', label: 'Action (system)' },
    { color: 'bg-gray-400', label: 'Terminal / End' },
  ];

  return React.createElement('div', {
    className: `rounded-lg border p-4 ${className || ''}`,
    'data-testid': 'flow-legend',
  },
    React.createElement('h4', { className: 'text-sm font-semibold mb-3' }, 'Legend'),
    React.createElement('div', { className: 'flex flex-wrap gap-4' },
      items.map((item) =>
        React.createElement('div', { key: item.label, className: 'flex items-center gap-2 text-xs text-muted-foreground' },
          React.createElement('span', { className: `w-3 h-3 rounded-full ${item.color}` }),
          item.label,
        ),
      ),
    ),
  );
}

// ─── Phone Mockup ───────────────────────────────────────────────

interface PhoneMockupProps {
  selectedNodeId: string | null;
  nodeData: { title: string; messageText?: string | null; templateSlug?: string | null } | null;
  businessName: string;
  locale: string;
}

export function PhoneMockup({ selectedNodeId, nodeData, businessName, locale }: PhoneMockupProps): JSX.Element {
  const messageContent = nodeData?.messageText || nodeData?.title || selectedNodeId || '';

  return React.createElement('div', {
    className: 'w-[280px] h-[500px] rounded-[2rem] border-4 border-gray-800 bg-gray-900 p-2 shadow-xl',
    'data-testid': 'phone-mockup',
  },
    React.createElement('div', { className: 'w-full h-full rounded-[1.5rem] bg-white dark:bg-gray-950 overflow-hidden flex flex-col' },
      // Status bar
      React.createElement('div', { className: 'bg-[#075E54] text-white px-4 py-3 flex items-center gap-3' },
        React.createElement('div', { className: 'w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs' }, 'W'),
        React.createElement('div', null,
          React.createElement('div', { className: 'text-sm font-semibold' }, businessName),
          React.createElement('div', { className: 'text-xs opacity-70' }, 'online'),
        ),
      ),
      // Chat area
      React.createElement('div', { className: 'flex-1 p-3 space-y-2 bg-[#ECE5DD] dark:bg-gray-900 overflow-auto flex flex-col justify-end' },
        selectedNodeId
          ? React.createElement('div', { className: 'space-y-1.5' },
              // Step label
              React.createElement('div', { className: 'text-[10px] text-center text-gray-500 dark:text-gray-400' },
                nodeData?.title || selectedNodeId,
              ),
              // WhatsApp-style outbound bubble
              React.createElement('div', { className: 'flex justify-end' },
                React.createElement('div', {
                  className: 'rounded-lg px-3 py-2 max-w-[85%] text-xs shadow-sm leading-relaxed',
                  style: { backgroundColor: '#dcf8c6', color: '#111b21', wordBreak: 'break-word' as const },
                },
                  React.createElement('p', { className: 'whitespace-pre-wrap' }, messageContent),
                  // Template slug badge
                  nodeData?.templateSlug && React.createElement('div', {
                    className: 'mt-1.5 pt-1 border-t border-black/5 flex items-center gap-1',
                  },
                    React.createElement('span', {
                      className: 'text-[9px] font-mono px-1.5 py-0.5 rounded bg-black/5 text-gray-500',
                    }, `📄 ${nodeData.templateSlug}`),
                  ),
                  // Timestamp
                  React.createElement('div', { className: 'flex justify-end mt-0.5' },
                    React.createElement('span', { className: 'text-[9px] opacity-40' },
                      new Date().toLocaleTimeString(locale === 'pt-BR' ? 'pt-BR' : 'en', { hour: '2-digit', minute: '2-digit' }),
                    ),
                  ),
                ),
              ),
            )
          : React.createElement('div', { className: 'text-center text-xs text-muted-foreground px-4' },
              locale === 'pt-BR'
                ? 'Selecione um nó no fluxo para ver a prévia da conversa'
                : 'Select a node in the flow to preview the conversation'
            ),
      ),
    ),
  );
}

// ─── Flow Stage Panel ───────────────────────────────────────────

interface FlowStagePanelProps {
  flowId: string;
  stageId: string;
  stageName: string;
  stateType: string;
  open: boolean;
  onConversationSelect: (id: string) => void;
  onClose: () => void;
  locale: string;
  useDataHook?: boolean;
}

export function FlowStagePanel({ stageName, stateType, open, onClose, locale }: FlowStagePanelProps): JSX.Element {
  if (!open) return null as any;

  return React.createElement('div', {
    className: 'fixed inset-y-0 right-0 w-96 bg-background border-l shadow-xl z-50 flex flex-col',
    'data-testid': 'flow-stage-panel',
  },
    React.createElement('div', { className: 'flex items-center justify-between p-4 border-b' },
      React.createElement('div', null,
        React.createElement('h3', { className: 'font-semibold' }, stageName),
        React.createElement('p', { className: 'text-xs text-muted-foreground capitalize' }, stateType),
      ),
      React.createElement('button', {
        onClick: onClose,
        className: 'p-1 rounded hover:bg-muted',
        'aria-label': 'Close panel',
      }, '\u2715'),
    ),
    React.createElement('div', { className: 'flex-1 p-4 text-sm text-muted-foreground' },
      locale === 'pt-BR'
        ? 'Nenhuma conversa nesta etapa ainda.'
        : 'No conversations at this stage yet.',
    ),
  );
}

// ─── Flow Funnel Chart ──────────────────────────────────────────

interface FlowFunnelChartProps {
  stages: Array<{ id: string; name: string; count: number; dropOff?: number }>;
  flowName: string;
  showDropOff?: boolean;
  onStageClick?: (stageId: string) => void;
}

export function FlowFunnelChart({ stages, flowName, showDropOff, onStageClick }: FlowFunnelChartProps): JSX.Element {
  const maxCount = Math.max(...stages.map((s) => s.count), 1);

  return React.createElement('div', {
    className: 'space-y-2',
    'data-testid': 'flow-funnel-chart',
  },
    stages.map((stage, i) => {
      const widthPct = Math.max((stage.count / maxCount) * 100, 10);
      return React.createElement('button', {
        key: stage.id,
        onClick: () => onStageClick?.(stage.id),
        className: 'w-full text-left flex items-center gap-3 group hover:bg-muted/50 rounded-lg p-2 transition-colors',
        'data-testid': `funnel-stage-${stage.id}`,
      },
        React.createElement('span', { className: 'text-xs text-muted-foreground w-4' }, i + 1),
        React.createElement('div', { className: 'flex-1' },
          React.createElement('div', { className: 'flex items-center justify-between text-xs mb-1' },
            React.createElement('span', { className: 'font-medium' }, stage.name),
            React.createElement('span', { className: 'text-muted-foreground' }, stage.count),
          ),
          React.createElement('div', { className: 'h-2 bg-muted rounded-full overflow-hidden' },
            React.createElement('div', {
              className: 'h-full bg-primary rounded-full transition-all',
              style: { width: `${widthPct}%` },
            }),
          ),
        ),
        showDropOff && stage.dropOff != null && stage.dropOff > 0 &&
          React.createElement('span', { className: 'text-xs text-red-500' }, `-${stage.dropOff}%`),
      );
    }),
  );
}
